// Hex grid algorithms including A* pathfinding for corridors

import type { Hex, Point, HexLayout, Room } from "../types.ts";
import {
  hexDistance,
  hexNeighbors,
  hexToKey,
  keyToHex,
  hexToPixel,
  pixelToHex,
  hexLineDraw,
} from "./coordinates.ts";
import type { DungeonModel } from "../model/dungeon.ts";

/**
 * Priority queue node for A* pathfinding
 */
interface AStarNode {
  hex: Hex;
  gScore: number;
  fScore: number;
}

/**
 * A* pathfinding on hex grid.
 * @param start - Starting hex
 * @param end - Ending hex
 * @param isPassable - Function to determine if a hex is passable
 * @returns Array of hexes from start to end (inclusive), or empty array if no path found
 */
export function aStarPath(
  start: Hex,
  end: Hex,
  isPassable: (hex: Hex) => boolean
): Hex[] {
  const startKey = hexToKey(start);
  const endKey = hexToKey(end);

  // Check if start and end are passable
  if (!isPassable(start) || !isPassable(end)) {
    return [];
  }

  // Maps to store costs and paths
  const gScore = new Map<string, number>(); // Cost from start to node
  const fScore = new Map<string, number>(); // Estimated total cost (g + h)
  const cameFrom = new Map<string, string>(); // Track path
  const openSet = new Set<string>([startKey]); // Nodes to explore
  const closedSet = new Set<string>(); // Already explored

  gScore.set(startKey, 0);
  fScore.set(startKey, hexDistance(start, end));

  while (openSet.size > 0) {
    // Get node with lowest fScore
    let current: string | null = null;
    let lowestFScore = Infinity;

    for (const key of openSet) {
      const score = fScore.get(key) ?? Infinity;
      if (score < lowestFScore) {
        lowestFScore = score;
        current = key;
      }
    }

    if (current === null) {
      return []; // No path found
    }

    // Found the goal
    if (current === endKey) {
      return reconstructPath(cameFrom, current);
    }

    openSet.delete(current);
    closedSet.add(current);

    const currentHex = keyToHex(current);
    const currentGScore = gScore.get(current) ?? Infinity;

    // Check all neighbors
    for (const neighbor of hexNeighbors(currentHex)) {
      if (!isPassable(neighbor)) {
        continue;
      }

      const neighborKey = hexToKey(neighbor);

      if (closedSet.has(neighborKey)) {
        continue;
      }

      // Cost to reach this neighbor (always 1 per hex in uniform grid)
      const tentativeGScore = currentGScore + 1;

      if (!openSet.has(neighborKey)) {
        openSet.add(neighborKey);
      } else if (
        tentativeGScore >= (gScore.get(neighborKey) ?? Infinity)
      ) {
        // Not a better path
        continue;
      }

      // This is the best path so far to this neighbor
      cameFrom.set(neighborKey, current);
      gScore.set(neighborKey, tentativeGScore);
      fScore.set(
        neighborKey,
        tentativeGScore + hexDistance(neighbor, end)
      );
    }
  }

  // No path found
  return [];
}

/**
 * Reconstruct path from A* cameFrom map
 */
function reconstructPath(
  cameFrom: Map<string, string>,
  current: string
): Hex[] {
  const path: Hex[] = [keyToHex(current)];

  while (cameFrom.has(current)) {
    current = cameFrom.get(current)!;
    path.unshift(keyToHex(current));
  }

  return path;
}

/**
 * Find path for a corridor between two rooms.
 * Follows the user's drawn stroke path rather than finding shortest path.
 * @param fromRoom - Starting room
 * @param toRoom - Ending room
 * @param dungeon - The dungeon model
 * @param layout - Hex layout for pixel conversion
 * @param strokePoints - User-drawn stroke points to guide the path
 * @returns Array of hex keys for the corridor path
 */
export function findCorridorPath(
  fromRoom: Room,
  toRoom: Room,
  dungeon: DungeonModel,
  layout: HexLayout,
  strokePoints: Point[]
): string[] {
  if (strokePoints.length < 2) {
    return [];
  }

  // Convert the stroke points directly to hex path, following the user's drawn line
  const strokePath = strokeToHexPath(strokePoints, layout);

  if (strokePath.length === 0) {
    return [];
  }

  // Validate the path doesn't go through other rooms
  const isPassable = (hex: Hex): boolean => {
    const key = hexToKey(hex);

    // Allow hexes in from/to rooms
    if (fromRoom.hexes.has(key) || toRoom.hexes.has(key)) {
      return true;
    }

    // Block hexes that are in other rooms
    const room = dungeon.getRoom(key);
    if (room && room.id !== fromRoom.id && room.id !== toRoom.id) {
      return false;
    }

    // Empty space is passable
    return true;
  };

  // Filter out any hexes that would collide with other rooms
  const validPath: string[] = [];
  for (const hex of strokePath) {
    if (isPassable(hex)) {
      validPath.push(hexToKey(hex));
    }
  }

  // Remove duplicates while preserving order
  const seen = new Set<string>();
  const uniquePath: string[] = [];
  for (const key of validPath) {
    if (!seen.has(key)) {
      seen.add(key);
      uniquePath.push(key);
    }
  }

  return uniquePath;
}

/**
 * Convert a stroke (array of pixel points) to a path of hexes.
 * Traces through all hexes the stroke passes through.
 * @param points - Array of pixel coordinates from user's stroke
 * @param layout - Hex layout for coordinate conversion
 * @returns Array of hexes along the stroke path
 */
export function strokeToHexPath(points: Point[], layout: HexLayout): Hex[] {
  if (points.length === 0) {
    return [];
  }

  if (points.length === 1) {
    return [pixelToHex(points[0], layout)];
  }

  const path: Hex[] = [];

  // Convert each segment of the stroke to hexes using hex line drawing
  for (let i = 0; i < points.length - 1; i++) {
    const startHex = pixelToHex(points[i], layout);
    const endHex = pixelToHex(points[i + 1], layout);

    // Get all hexes along this segment
    const segmentHexes = hexLineDraw(startHex, endHex);

    // Add to path (hexLineDraw includes both endpoints, so skip first on subsequent segments)
    if (i === 0) {
      path.push(...segmentHexes);
    } else {
      // Skip first hex as it's the same as last hex of previous segment
      path.push(...segmentHexes.slice(1));
    }
  }

  return path;
}

/**
 * Recalculate a corridor path between two rooms using A*.
 * Unlike findCorridorPath, this allows passing through other rooms/corridors.
 * @param fromRoom - Starting room
 * @param toRoom - Ending room
 * @param layout - Hex layout for pixel conversion
 * @returns Array of hex keys for the corridor path (excluding hexes inside the rooms)
 */
export function recalculateCorridorPath(
  fromRoom: Room,
  toRoom: Room,
  layout: HexLayout
): string[] {
  // Find the closest edge hexes between the two rooms
  const fromEdge = getEdgeHexes(fromRoom.hexes);
  const toEdge = getEdgeHexes(toRoom.hexes);

  if (fromEdge.size === 0 || toEdge.size === 0) {
    return [];
  }

  // Find the pair of edge hexes that are closest to each other
  let bestFromHex: Hex | null = null;
  let bestToHex: Hex | null = null;
  let bestDistance = Infinity;

  for (const fromKey of fromEdge) {
    const fromHex = keyToHex(fromKey);
    for (const toKey of toEdge) {
      const toHex = keyToHex(toKey);
      const dist = hexDistance(fromHex, toHex);
      if (dist < bestDistance) {
        bestDistance = dist;
        bestFromHex = fromHex;
        bestToHex = toHex;
      }
    }
  }

  if (!bestFromHex || !bestToHex) {
    return [];
  }

  // Use A* to find path - everything is passable
  const isPassable = (_hex: Hex): boolean => true;

  const path = aStarPath(bestFromHex, bestToHex, isPassable);

  if (path.length === 0) {
    return [];
  }

  // Convert to keys and filter out hexes inside either room
  const corridorPath: string[] = [];
  for (const hex of path) {
    const key = hexToKey(hex);
    if (!fromRoom.hexes.has(key) && !toRoom.hexes.has(key)) {
      corridorPath.push(key);
    }
  }

  return corridorPath;
}

/**
 * Recalculate a corridor path between two rooms, routing through intermediate rooms.
 * @param fromRoom - Starting room
 * @param toRoom - Ending room
 * @param throughRooms - Array of rooms the corridor should pass through (in order)
 * @param layout - Hex layout for pixel conversion
 * @returns Array of hex keys for the corridor path
 */
export function recalculateCorridorPathThroughRooms(
  fromRoom: Room,
  toRoom: Room,
  throughRooms: Room[],
  layout: HexLayout
): string[] {
  if (throughRooms.length === 0) {
    return recalculateCorridorPath(fromRoom, toRoom, layout);
  }

  // Build the sequence of rooms to traverse
  const roomSequence = [fromRoom, ...throughRooms, toRoom];
  const fullPath: string[] = [];

  // Calculate path segments between consecutive rooms
  for (let i = 0; i < roomSequence.length - 1; i++) {
    const currentRoom = roomSequence[i];
    const nextRoom = roomSequence[i + 1];

    const currentEdge = getEdgeHexes(currentRoom.hexes);
    const nextEdge = getEdgeHexes(nextRoom.hexes);

    if (currentEdge.size === 0 || nextEdge.size === 0) {
      continue;
    }

    // Find closest edge hex pair between these two rooms
    let bestFromHex: Hex | null = null;
    let bestToHex: Hex | null = null;
    let bestDistance = Infinity;

    for (const fromKey of currentEdge) {
      const fromHex = keyToHex(fromKey);
      for (const toKey of nextEdge) {
        const toHex = keyToHex(toKey);
        const dist = hexDistance(fromHex, toHex);
        if (dist < bestDistance) {
          bestDistance = dist;
          bestFromHex = fromHex;
          bestToHex = toHex;
        }
      }
    }

    if (!bestFromHex || !bestToHex) {
      continue;
    }

    // Use A* to find path - everything is passable
    const isPassable = (_hex: Hex): boolean => true;
    const segmentPath = aStarPath(bestFromHex, bestToHex, isPassable);

    // Add segment to full path, excluding hexes inside rooms
    for (const hex of segmentPath) {
      const key = hexToKey(hex);
      // Don't add hexes that are inside the start/end rooms
      // But DO include hexes inside intermediate rooms (they're part of the path)
      if (!fromRoom.hexes.has(key) && !toRoom.hexes.has(key)) {
        // Avoid duplicates at segment boundaries
        if (fullPath.length === 0 || fullPath[fullPath.length - 1] !== key) {
          fullPath.push(key);
        }
      }
    }
  }

  return fullPath;
}

/**
 * Find hexes on the edge of a region (have neighbors outside the region).
 * @param hexes - Set of hex keys defining the region
 * @returns Set of hex keys that are on the edge
 */
export function getEdgeHexes(hexes: Set<string>): Set<string> {
  const edgeHexes = new Set<string>();

  for (const hexKey of hexes) {
    const hex = keyToHex(hexKey);
    const neighbors = hexNeighbors(hex);

    // Check if any neighbor is outside the region
    for (const neighbor of neighbors) {
      const neighborKey = hexToKey(neighbor);
      if (!hexes.has(neighborKey)) {
        edgeHexes.add(hexKey);
        break; // This hex is on the edge
      }
    }
  }

  return edgeHexes;
}

/**
 * Find the hex in a set closest to a pixel point.
 * @param point - Pixel coordinates
 * @param hexes - Set of hex keys to search
 * @param layout - Hex layout for pixel conversion
 * @returns The closest hex
 */
export function findClosestHexInSet(
  point: Point,
  hexes: Set<string>,
  layout: HexLayout
): Hex {
  let closestHex: Hex | null = null;
  let minDistance = Infinity;

  for (const hexKey of hexes) {
    const hex = keyToHex(hexKey);
    const hexCenter = hexToPixel(hex, layout);

    // Calculate Euclidean distance in pixel space
    const dx = hexCenter.x - point.x;
    const dy = hexCenter.y - point.y;
    const distance = Math.sqrt(dx * dx + dy * dy);

    if (distance < minDistance) {
      minDistance = distance;
      closestHex = hex;
    }
  }

  // Should never happen if hexes is non-empty, but provide fallback
  if (closestHex === null) {
    // Return the first hex in the set
    const firstKey = hexes.values().next().value as string;
    return keyToHex(firstKey);
  }

  return closestHex;
}

/**
 * Find the neighbor hex most aligned with a given direction.
 * Uses dot product to score alignment.
 * @param hex - The source hex
 * @param dx - X component of direction (in pixel space)
 * @param dy - Y component of direction (in pixel space)
 * @param layout - Hex layout for coordinate conversion
 * @returns The neighbor most aligned with the direction
 */
export function findOutwardNeighbor(
  hex: Hex,
  dx: number,
  dy: number,
  layout: HexLayout
): Hex {
  const neighbors = hexNeighbors(hex);
  const hexCenter = hexToPixel(hex, layout);

  let bestNeighbor = neighbors[0];
  let bestScore = -Infinity;

  for (const neighbor of neighbors) {
    const neighborCenter = hexToPixel(neighbor, layout);
    // Direction from hex to neighbor
    const ndx = neighborCenter.x - hexCenter.x;
    const ndy = neighborCenter.y - hexCenter.y;

    // Dot product (alignment score)
    const score = dx * ndx + dy * ndy;

    if (score > bestScore) {
      bestScore = score;
      bestNeighbor = neighbor;
    }
  }

  return bestNeighbor;
}

/**
 * Calculate hexes to add for radial expansion of a room.
 * Expands outward from the center in all directions proportionally.
 * @param roomHexes - Set of hex keys defining the room
 * @param center - Center point to expand from (in pixel coordinates)
 * @param layout - Hex layout for coordinate conversion
 * @returns Set of hex keys for expansion (not including existing room hexes)
 */
export function calculateRadialExpansion(
  roomHexes: Set<string>,
  center: Point,
  layout: HexLayout
): Set<string> {
  const edgeHexes = getEdgeHexes(roomHexes);
  const expansionHexes = new Set<string>();

  for (const hexKey of edgeHexes) {
    const hex = keyToHex(hexKey);
    const hexCenter = hexToPixel(hex, layout);

    // Direction from center to this edge hex
    const dx = hexCenter.x - center.x;
    const dy = hexCenter.y - center.y;

    // Skip if this hex is at the center (no clear outward direction)
    if (Math.abs(dx) < 0.1 && Math.abs(dy) < 0.1) continue;

    // Find the neighbor hex in that direction
    const outwardHex = findOutwardNeighbor(hex, dx, dy, layout);
    const outwardKey = hexToKey(outwardHex);

    // Only add if not already in the room
    if (!roomHexes.has(outwardKey)) {
      expansionHexes.add(outwardKey);
    }
  }

  return expansionHexes;
}

/**
 * Calculate hexes to remove for radial contraction of a room.
 * Removes edge hexes furthest from center.
 * @param roomHexes - Set of hex keys defining the room
 * @param center - Center point to contract toward (in pixel coordinates)
 * @param layout - Hex layout for coordinate conversion
 * @returns Set of hex keys to remove (must leave at least 1 hex)
 */
export function calculateRadialContraction(
  roomHexes: Set<string>,
  center: Point,
  layout: HexLayout
): Set<string> {
  if (roomHexes.size <= 1) return new Set(); // Can't contract below 1 hex

  const edgeHexes = getEdgeHexes(roomHexes);

  // Score each edge hex by distance from center (higher = further = remove first)
  const scoredHexes: Array<{ key: string; distance: number }> = [];

  for (const hexKey of edgeHexes) {
    const hex = keyToHex(hexKey);
    const hexCenter = hexToPixel(hex, layout);
    const dx = hexCenter.x - center.x;
    const dy = hexCenter.y - center.y;
    const distance = Math.sqrt(dx * dx + dy * dy);
    scoredHexes.push({ key: hexKey, distance });
  }

  // Sort by distance (furthest first)
  scoredHexes.sort((a, b) => b.distance - a.distance);

  // Remove roughly half of edge hexes (or at least 1), but keep at least 1 hex total
  const removeCount = Math.min(
    Math.max(1, Math.floor(scoredHexes.length / 2)),
    roomHexes.size - 1
  );

  const contractionHexes = new Set<string>();
  for (let i = 0; i < removeCount; i++) {
    contractionHexes.add(scoredHexes[i].key);
  }

  return contractionHexes;
}

/**
 * Find the best position for a room label.
 * Prefers interior hexes (not on edge) closest to the geometric center.
 * For donut-shaped rooms, finds a hex that's actually inside the room.
 *
 * @param roomHexes - Set of hex keys in the room
 * @param layout - Hex layout for coordinate conversion
 * @param occupiedPositions - Optional set of positions already used by other labels
 * @returns The best pixel position for the label
 */
export function findBestLabelPosition(
  roomHexes: Set<string>,
  layout: HexLayout,
  occupiedPositions?: Array<{ x: number; y: number; width: number; height: number }>
): Point {
  if (roomHexes.size === 0) {
    return { x: 0, y: 0 };
  }

  // Calculate geometric center
  let sumX = 0, sumY = 0;
  for (const key of roomHexes) {
    const hex = keyToHex(key);
    const center = hexToPixel(hex, layout);
    sumX += center.x;
    sumY += center.y;
  }
  const geometricCenter: Point = {
    x: sumX / roomHexes.size,
    y: sumY / roomHexes.size,
  };

  // Get edge hexes and find interior hexes
  const edgeHexes = getEdgeHexes(roomHexes);
  const interiorHexes = new Set<string>();
  for (const hexKey of roomHexes) {
    if (!edgeHexes.has(hexKey)) {
      interiorHexes.add(hexKey);
    }
  }

  // Determine which set to search for best position
  // Prefer interior hexes if available, otherwise use all hexes
  const searchSet = interiorHexes.size > 0 ? interiorHexes : roomHexes;

  // Find the hex closest to geometric center
  let bestHex: string | null = null;
  let bestDistance = Infinity;

  for (const hexKey of searchSet) {
    const hex = keyToHex(hexKey);
    const hexCenter = hexToPixel(hex, layout);
    const dx = hexCenter.x - geometricCenter.x;
    const dy = hexCenter.y - geometricCenter.y;
    const distance = Math.sqrt(dx * dx + dy * dy);

    if (distance < bestDistance) {
      bestDistance = distance;
      bestHex = hexKey;
    }
  }

  // Get the pixel position of the best hex
  let labelPosition: Point;
  if (bestHex) {
    const hex = keyToHex(bestHex);
    labelPosition = hexToPixel(hex, layout);
  } else {
    labelPosition = geometricCenter;
  }

  // If there are occupied positions, check for overlaps and adjust if needed
  if (occupiedPositions && occupiedPositions.length > 0) {
    const labelHeight = layout.size * 0.8; // Approximate label height
    const labelWidth = layout.size * 3; // Approximate label width

    for (const occupied of occupiedPositions) {
      // Check for overlap
      const overlapX = Math.abs(labelPosition.x - occupied.x) < (labelWidth + occupied.width) / 2;
      const overlapY = Math.abs(labelPosition.y - occupied.y) < (labelHeight + occupied.height) / 2;

      if (overlapX && overlapY) {
        // Try to find an alternative position by checking other hexes
        let foundAlternative = false;
        const sortedHexes = [...searchSet].map(key => {
          const hex = keyToHex(key);
          const center = hexToPixel(hex, layout);
          const dx = center.x - geometricCenter.x;
          const dy = center.y - geometricCenter.y;
          return { key, center, distance: Math.sqrt(dx * dx + dy * dy) };
        }).sort((a, b) => a.distance - b.distance);

        for (const { center } of sortedHexes) {
          let hasOverlap = false;
          for (const occ of occupiedPositions) {
            const ox = Math.abs(center.x - occ.x) < (labelWidth + occ.width) / 2;
            const oy = Math.abs(center.y - occ.y) < (labelHeight + occ.height) / 2;
            if (ox && oy) {
              hasOverlap = true;
              break;
            }
          }
          if (!hasOverlap) {
            labelPosition = center;
            foundAlternative = true;
            break;
          }
        }

        // If no non-overlapping position found, offset vertically
        if (!foundAlternative) {
          labelPosition = {
            x: labelPosition.x,
            y: labelPosition.y + labelHeight * 1.2,
          };
        }
        break;
      }
    }
  }

  return labelPosition;
}
